import React, { useState, useEffect } from "react";
import { FaChevronDown, FaChevronUp, FaSearch } from "react-icons/fa";
import floralCorner from "../assets/65102635_9567363.png";

const defaultFaqs = [
  {
    question: "How do I book an event?",
    answer: "Simply fill out our contact form, and we'll get in touch to finalize your booking and payment.",
  },
  {
    question: "Is payment secure?",
    answer: "Yes! We use Razorpay, a leading payment gateway, for all transactions.",
  },
  {
    question: "Can I customize my event package?",
    answer: "Absolutely! Our team will work closely with you to tailor every detail of your event to your preferences.",
  },
  {
    question: "Do you provide on-site coordination?",
    answer: "Yes, our expert coordinators will be present to ensure everything runs seamlessly on your special day.",
  },
];

const maroon = "#6b1331";
const blush = "#fbeff5";
const gold = "#e4b051";
const goldLight = "#fff9f0";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [content, setContent] = useState(null);

  // Load CMS content
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/content/faqs.json", { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          setContent(data);
        }
      } catch (e) {
        console.warn("Using default FAQ content");
      }
    })();
  }, []);

  const faqs = content?.faqs || defaultFaqs;
  const title = content?.title || "Frequently Asked Questions";

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      style={{
        minHeight: "auto",
        width: "100%",
        background: blush,
        padding: "40px 0 60px 0",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Smaller floral decorations */}
      <img
        src={floralCorner}
        alt=""
        style={{
          position: "absolute",
          top: -20,
          left: -20,
          width: "200px",
          opacity: 0.5,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />
      <img
        src={floralCorner}
        alt=""
        style={{
          position: "absolute",
          bottom: -20,
          right: -20,
          width: "200px",
          opacity: 0.5,
          transform: "scaleX(-1)",
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 4px 24px rgba(107,19,49,0.06)",
          maxWidth: 640,
          width: "92%",
          padding: "32px 28px",
          margin: "0 auto",
          zIndex: 1,
        }}
        id="faq"
      >
        {/* Compact heading */}
        <h2
          style={{
            fontFamily: `'Playfair Display', serif`,
            color: maroon,
            fontWeight: 700,
            textAlign: "center",
            letterSpacing: 0.5,
            fontSize: "clamp(1.5rem, 4vw, 2rem)",
            marginBottom: 16,
            lineHeight: 1.3,
          }}
        >
          {title.split(' ').slice(0, 2).join(' ')}{" "}
          <span style={{ color: gold, fontStyle: "italic" }}>{title.split(' ').slice(2).join(' ')}</span>
        </h2>

        {/* Compact search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            maxWidth: 300,
            margin: "0 auto 24px auto",
            background: goldLight,
            borderRadius: 24,
            boxShadow: "0 1px 4px rgba(107,19,49,0.05)",
            padding: "6px 14px",
          }}
        >
          <FaSearch style={{ color: gold, fontSize: 14, marginRight: 8 }} />
          <input
            type="text"
            placeholder="Search FAQ..."
            style={{
              border: "none",
              background: "transparent",
              fontSize: 14,
              outline: "none",
              flex: 1,
              color: maroon,
              fontFamily: "inherit",
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Compact accordion */}
        <div>
          {filteredFaqs.length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: "#888",
                padding: "20px 0",
                fontSize: 15,
                fontStyle: "italic",
              }}
            >
              No matching FAQs found.
            </div>
          )}

          {filteredFaqs.map((faq, idx) => {
            const actualIdx = faqs.indexOf(faq);
            const isOpen = openIndex === actualIdx;

            return (
              <div
                key={actualIdx}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  marginBottom: 12,
                  boxShadow: isOpen
                    ? "0 4px 12px rgba(228,176,81,0.08)"
                    : "0 1px 6px rgba(107,19,49,0.05)",
                  overflow: "hidden",
                  border: `1px solid ${isOpen ? gold : "#f0e6e0"}`,
                  transition: "all 0.2s ease",
                }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : actualIdx)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${actualIdx}`}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    outline: "none",
                    padding: "14px 18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: `'Playfair Display', serif`,
                    fontSize: "clamp(0.95rem, 2.5vw, 1.05rem)",
                    color: maroon,
                    fontWeight: 600,
                    textAlign: "left",
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ flex: 1, paddingRight: 12 }}>{faq.question}</span>
                  <span
                    style={{
                      color: gold,
                      fontSize: 16,
                      transition: "transform 0.2s",
                      transform: isOpen ? "rotate(180deg)" : "none",
                      flexShrink: 0,
                    }}
                  >
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </button>
                
                <div
                  id={`faq-answer-${actualIdx}`}
                  style={{
                    maxHeight: isOpen ? 200 : 0,
                    transition: "max-height 0.3s ease, padding 0.3s ease",
                    overflow: "hidden",
                    padding: isOpen ? "0 18px 14px 18px" : "0 18px",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Inter, Arial, sans-serif",
                      color: "#555",
                      fontSize: "clamp(0.875rem, 2vw, 0.95rem)",
                      lineHeight: 1.5,
                      opacity: isOpen ? 1 : 0,
                      transition: "opacity 0.25s ease",
                    }}
                  >
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}